import { sel } from "../../academy/selectors";
import { TutorialDefinition } from "../types";

/**
 * Academy lesson 1: upload a narrative recording and let the AI draft the
 * report. Started from the lesson page ("Start the walkthrough"); it is never
 * auto-started. Every step is `disableBeacon` because the officer is doing
 * the clicking: a beacon they would have to find first defeats the point.
 */
const tour: TutorialDefinition = {
  id: "first-narrative-report",
  version: "1.0.0",
  releasedAt: "2026-10-28T00:00:00.000Z",
  category: "onboarding",
  title: "Your first report",
  description: "Upload a narrative recording and let the AI draft the report.",
  triggerRoute: /^\/cases(\/.*)?$/,
  startRoute: "/cases",
  lessonId: "first-narrative-report",
  autoStart: false,
  steps: [
    {
      target: sel("newCaseDropdown"),
      title: "Start a new case",
      content:
        "Every report starts here. Click New Case, then choose Upload Files.",
      disableBeacon: true,
      placement: "right",
      advanceOn: { selector: sel("newCaseUpload"), event: "click" },
      hint: "Click New Case, then Upload Files to continue",
      spotlightClicks: true,
    },
    {
      target: sel("uploadDropzone"),
      waitFor: sel("uploadDropzone"),
      title: "Add your recording",
      content:
        "Drag the sample dictation you downloaded from the Academy here, or click to browse. MP3, M4A, WAV, and MP4 are all fine.",
      disableBeacon: true,
      placement: "bottom",
      // A file pick is a `change` on the hidden input, not a click.
      advanceOn: {
        selector: `${sel("uploadDropzone")} input[type="file"]`,
        event: "change",
      },
      hint: "Choose a file to continue",
      spotlightClicks: true,
    },
    {
      target: sel("uploadTypeNarrative"),
      waitFor: sel("uploadTypeNarrative"),
      title: "Narrative or Interaction",
      content:
        "Narrative means you are describing what happened in your own words. Interaction is a recorded conversation (an interview, a field contact) and gets speaker separation. Leave this on Narrative.",
      disableBeacon: true,
      placement: "left",
    },
    {
      target: sel("uploadReportSettings"),
      waitFor: sel("uploadReportSettings"),
      title: "Report Settings",
      content:
        "Pick a report template, the detail level (Concise / Standard / Detailed), and whether to include timestamps in the narrative. These default to your Report Preferences, so you can usually leave them.",
      disableBeacon: true,
      placement: "top",
    },
    {
      target: sel("uploadSubmit"),
      waitFor: sel("uploadSubmit"),
      title: "Upload",
      content:
        "Click Upload (1 file). The audio is transcribed and a first draft is written in your style. You can leave this page while it works.",
      disableBeacon: true,
      placement: "top",
      advanceOn: { selector: sel("uploadSubmit"), event: "click" },
      hint: "Click Upload to continue",
      spotlightClicks: true,
    },
    {
      target: sel("editorProcessing"),
      waitFor: sel("editorProcessing"),
      // Creating the case and routing to it can take a few seconds.
      waitTimeoutMs: 30000,
      title: "Drafting your report",
      content:
        "Processing usually takes one to three minutes. The case already appears in your list, and the draft, score, and suggestions arrive when it finishes. Open the Academy lesson 'Review the draft' next.",
      disableBeacon: true,
      placement: "center",
    },
  ],
};

export default tour;
